import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { Label } from '@/components/ui/shadcn/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/shadcn/card'
import { useBrand } from '@/context/brandContext'
import { toast } from 'sonner'
import { Building2 } from 'lucide-react'
import { UpgradeModal } from '@/components/upgrade-modal'

export default function BrandNewPage() {
  const navigate = useNavigate()
  const { createBrand } = useBrand()
  const [name, setName] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      toast.error('Please enter a brand name')
      return
    }

    setIsCreating(true)
    try {
      await createBrand(name.trim())
      toast.success('Brand created successfully')
      // Redirect to company setup for the new brand
      navigate('/company-setup')
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      if (message.includes('Usage limit exceeded')) {
        setShowUpgradeModal(true)
      } else {
        toast.error(message || 'Failed to create brand')
      }
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="bg-primary/10 mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full">
            <Building2 className="text-primary h-6 w-6" />
          </div>
          <CardTitle className="text-center text-2xl">Create Brand</CardTitle>
          <CardDescription className="text-center">
            Create a new brand to manage a separate company or identity
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Brand Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="e.g., My Company"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isCreating}
                autoFocus
              />
            </div>
            <Button type="submit" className="w-full" disabled={isCreating}>
              {isCreating ? 'Creating...' : 'Create Brand'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => navigate(-1)}
              disabled={isCreating}>
              Cancel
            </Button>
          </form>
        </CardContent>
      </Card>
      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        limitType="brands"
        surface="brand_new"
      />
    </div>
  )
}
